import React, {useEffect, useState, useRef, useMemo, useCallback} from "react";
import {useSearchParams} from "react-router-dom";
import {
    Box,
    Container,
    Typography,
    Paper,
    CssBaseline,
} from "@mui/material";
import {DashboardSkeleton} from "../components/skeletons";
import ErrorWithRetry from "../components/ErrorWithRetry";
import {ThemeProvider} from "@mui/material/styles";
import {Lock as LockIcon} from "@mui/icons-material";
import {useQuery} from "@tanstack/react-query";
import JsdhNotifikaceService from "../api/JsdhNotifikaceService/service/JsdhNotifikaceService";
import TechnikaServiceApi from "../api/JsdhNotifikaceService/service/TechnikaService";
import {Konfigurace, Udalost, DeviceTokenThemeResponse} from "../api/JsdhNotifikaceService/generated";
import type {UdalostRouteResponse} from "../api/JsdhNotifikaceService/generated/models/UdalostRoute";
import type {JednotkaDto} from "../api/JsdhNotifikaceService/generated/models/JednotkaDto";
import type {Odpoved} from "../api/JsdhNotifikaceService/generated/models/Odpoved";
import type {Technika} from "../api/JsdhNotifikaceService/generated/models/Technika";
import type {PoplachTechnikaResponse} from "../api/JsdhNotifikaceService/generated/models/PoplachTechnika";
import type {PosilovaNotifikaceLogResponse} from "../api/JsdhNotifikaceService/generated/models/PosilovaNotifikaceLog";
import ZadnyPoplach from "./ZadnyPoplach";
import BlackScreen from "../components/BlackScreen";
import PredpoplachDisplay from "../components/PredpoplachDisplay";
import { usePredpoplachAktivniQuery } from "../api/JsdhNotifikaceService/service/PredpoplachMutations";
import {useToast} from "../components/toast/useToast";
import {useAuth} from "../context/AuthContext";
import DetailPoplachu from "../components/DetailPoplachu";
import TechnikaWrapper from "../components/TechnikaWrapper";
import Layout from "../components/LayoutComponent";
import {QueryError} from "../api/QueryError";
import OdpovediWrapper from "../components/OdpovediWrapper";
import TechnikaUcastWrapper from "../components/TechnikaUcastWrapper";
import {createAppTheme} from "../theme/createAppTheme";
import {getColorScheme, DEFAULT_COLOR_SCHEME} from "../theme/colorSchemes";
import {useThemeMode} from "../theme/ThemeContext";
import {parsePoplachConfig} from "../api/JsdhNotifikaceService/generated";
import { Vyjezd2026Wrapper } from "../components/Vyjezd2026Wrapper";
import { RamecekNahleduNaVysku } from "../components/RamecekNahleduNaVysku";
import { usePoplachMockData } from "../hooks/usePoplachMockData";
import { useZalohaJinde } from "../hooks/useZalohaJinde";
import { useKiosekZije } from "../hooks/useKiosekZije";

const DashboardPage = () => {
    const [searchParams] = useSearchParams();
    const deviceToken = searchParams.get("deviceToken");
    const {isAuthenticated} = useAuth();

    const mockType = searchParams.get("mock");
    const parseIntParam = (key: string): number | undefined => {
        const v = searchParams.get(key);
        if (v === null) return undefined;
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? undefined : n;
    };
    const mapaLayoutRaw = searchParams.get('mapaLayout');
    const mapaLayoutParam = (mapaLayoutRaw === 'misto' || mapaLayoutRaw === 'trasa' ||
        mapaLayoutRaw === 'misto-trasa' || mapaLayoutRaw === 'off')
        ? mapaLayoutRaw : undefined;
    const nocniRezimRaw = searchParams.get('nocniRezim');
    const nocniRezimParam = (nocniRezimRaw === 'auto' || nocniRezimRaw === 'light' || nocniRezimRaw === 'dark')
        ? nocniRezimRaw : undefined;
    const pravidlaRaw = searchParams.get('pravidla');
    const pravidlaParam = pravidlaRaw === 'true' ? true : pravidlaRaw === 'false' ? false : undefined;
    const mapModeRaw = searchParams.get('mapMode');
    const mapModeParam = (mapModeRaw === 'auto' || mapModeRaw === 'fixed' || mapModeRaw === 'cycle')
        ? mapModeRaw : undefined;
    // ?orientace=vyska|sirka vynutí rozložení presetu vyjezd-2026 (náhled z adminu).
    // vyska navíc vykreslí rámeček 1080 × 1920 zmenšený na okno.
    const orientaceRaw = searchParams.get('orientace');
    const orientaceParam = (orientaceRaw === 'vyska' || orientaceRaw === 'sirka') ? orientaceRaw : undefined;
    const mockData = usePoplachMockData(mockType, {
        technika: parseIntParam('technika'),
        jedu: parseIntParam('jedu'),
        nejedu: parseIntParam('nejedu'),
        posily: parseIntParam('posily'),
        mapaLayout: mapaLayoutParam,
        mapMode: mapModeParam,
        fixedZoom: parseIntParam('fixedZoom'),
        zoom1: parseIntParam('zoom1'),
        zoom2: parseIntParam('zoom2'),
        interval: parseIntParam('interval'),
        pred: searchParams.get('pred') || undefined,
        udalost: searchParams.get('udalost') || undefined,
        popis: searchParams.get('popis') || undefined,
        upresneni: searchParams.get('upresneni') || undefined,
        nocniRezim: nocniRezimParam,
        pravidla: pravidlaParam,
    });
    const useRealData = !mockType;

    // Přístup povolen pokud je deviceToken NEBO je uživatel přihlášen NEBO mock režim
    const hasAccess = !!deviceToken || isAuthenticated || !!mockType;

    // Spec upozorneni na odpojeni 5.1: kioskova stranka hlasi BE, ze bezi (jen s device tokenem, ne v mock rezimu)
    useKiosekZije(deviceToken, useRealData);

    // Získání theme z kontextu (pro přihlášené uživatele)
    const {isDark: contextIsDark, colorScheme: contextColorScheme} = useThemeMode();

    // Query pro načtení theme z API (pro device token)
    const {data: deviceThemeData} = useQuery<DeviceTokenThemeResponse, QueryError>({
        queryKey: ["deviceTokenTheme", deviceToken],
        queryFn: () => JsdhNotifikaceService.getDeviceTokenTheme(deviceToken!),
        enabled: !!deviceToken && useRealData,
        staleTime: 20000,
        refetchInterval: 30000, // každých 30 sekund (pro detekci změn konfigurace)
        refetchOnWindowFocus: false,
    });

    // Určení zda použít dark mode
    const isDark = useMemo(() => {
        if (deviceToken && deviceThemeData?.theme) {
            if (deviceThemeData.theme === 'system') {
                return window.matchMedia('(prefers-color-scheme: dark)').matches;
            }
            return deviceThemeData.theme === 'dark';
        }
        return contextIsDark;
    }, [deviceToken, deviceThemeData?.theme, contextIsDark]);

    // Vytvoření theme pro dashboard
    const dashboardTheme = useMemo(() => {
        const scheme = getColorScheme(contextColorScheme || DEFAULT_COLOR_SCHEME);
        return createAppTheme(scheme, isDark);
    }, [contextColorScheme, isDark]);

    // Načíst konfiguraci z konfigurace tabulky (pro přihlášené uživatele bez device tokenu)
    const {data: displayConfigData} = useQuery<Konfigurace, QueryError>({
        queryKey: ["konfigurace", "POPLACH_DISPLAY_CONFIG"],
        queryFn: () => JsdhNotifikaceService.getKonfiguraceByKey("POPLACH_DISPLAY_CONFIG"),
        enabled: !deviceToken && isAuthenticated && useRealData,
        staleTime: 300000,
        retry: false,
    });

    // Parsovat konfiguraci poplachové obrazovky.
    // Záměrně závisíme na string hodnotách (poplachConfig JSON, hodnota), ne na wrapper objektech,
    // aby se reference poplachConfig (a vnořená mapa) neměnila při každém refetchu, kdy obsah JSON je beze změny.
    // Bez tohoto by MapZoomWrapper.useEffect resetoval cycle interval pri každém refetch tikutu deviceTokenTheme.
    const deviceTokenPoplachConfig = (deviceThemeData as { poplachConfig?: string } | undefined)?.poplachConfig;
    const displayConfigJson = displayConfigData?.hodnota;
    const poplachConfig = useMemo(
        () => parsePoplachConfig(deviceToken ? deviceTokenPoplachConfig : displayConfigJson),
        [deviceToken, deviceTokenPoplachConfig, displayConfigJson]
    );

    // Effective mapaLayout for route query
    const mapaLayout = poplachConfig.mapaLayout || 'misto';

    const {showToast} = useToast();
    const [errorToastShown, setErrorToastShown] = useState(false);
    const [, setAudioLoaded] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const lastConfigVersion = useRef<number | undefined>(undefined);

    // Po deaktivaci poplachu / po startu: zobrazit text po dobu casVyjezdMinuty, pak přepnout na BlackScreen
    const [postAlertIdle, setPostAlertIdle] = useState(false);
    const prevPoplachId = useRef<number | undefined>(undefined);

    // Načíst data jednotky předem a periodicky aktualizovat
    const {data: jednotka} = useQuery<JednotkaDto, QueryError>({
        queryKey: ["jednotka"],
        queryFn: () => JsdhNotifikaceService.getJednotka(),
        enabled: hasAccess && useRealData,
        staleTime: 300000,        // 5 minut
        refetchInterval: 300000,  // každých 5 minut aktualizovat
        refetchOnWindowFocus: false,
    });

    // Výpočet délky audio v minutách - pro 10+ minut odečíst 2 minuty
    const getAudioDurationMinutes = useCallback((casVyjezdMinuty: number | undefined): number => {
        const minutes = casVyjezdMinuty || 10;
        return minutes >= 10 ? minutes - 2 : minutes;
    }, []);

    const audioDurationMinutes = useMemo(
        () => getAudioDurationMinutes(jednotka?.casVyjezdMinuty),
        [jednotka?.casVyjezdMinuty, getAudioDurationMinutes]
    );

    const {
        data: aktivniPoplach,
        isLoading: isLoadingPoplach,
        isError: isErrorPoplach,
        isFetching: isFetchingPoplach,
        refetch: refetchPoplach,
    } = useQuery<Udalost, QueryError>({
        queryKey: ["aktivniPoplach", deviceToken],
        queryFn: () => JsdhNotifikaceService.getAktivniUdalost({ deviceToken: deviceToken || undefined }),
        enabled: hasAccess && useRealData,
        refetchInterval: 10000,              // každých 10 sekund
        staleTime: 8000,
        refetchIntervalInBackground: true,   // refetch i na pozadí
        refetchOnWindowFocus: false,
        refetchOnReconnect: true,
        retry: 2,
        networkMode: "always",
    });

    // Query na aktivní předpoplach (zobrazí se když není poplach)
    const { data: aktivniPredpoplach } = usePredpoplachAktivniQuery(
        deviceToken || undefined,
        { enabled: useRealData }
    );

    const {data: refreshConfig} = useQuery<Konfigurace, QueryError>({
            queryKey: ["refreshPage", deviceToken],
            queryFn: () => JsdhNotifikaceService.getRefreshPage(deviceToken || undefined),
            enabled: hasAccess && useRealData,
            refetchInterval: 300_000,         // každých 5 minut
            refetchOnWindowFocus: false,
            refetchOnReconnect: true,
        });

// Query na audio soubor
    const {
        data: audioBlob,
        refetch: refetchAudio,
        isFetching: isFetchingAudio,
    } = useQuery<Blob>({
        queryKey: ["poplachAudio", aktivniPoplach?.id, deviceToken],
        queryFn: () =>
            aktivniPoplach
                ? JsdhNotifikaceService.getAudioByPoplachId(aktivniPoplach.id, deviceToken || undefined)
                : Promise.resolve(new Blob()),
        enabled: Boolean(aktivniPoplach?.id) && hasAccess && useRealData,
        staleTime: Infinity,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        retry: false, // retry si řešíme ručně
    });

    // Route data query (async — trasa se pocita na pozadi)
    const {data: routeData} = useQuery<UdalostRouteResponse | null, QueryError>({
        queryKey: ["udalostRoute", aktivniPoplach?.id, deviceToken],
        queryFn: () => aktivniPoplach
            ? JsdhNotifikaceService.getUdalostRoute(aktivniPoplach.id, deviceToken || undefined)
            : Promise.resolve(null),
        enabled: Boolean(aktivniPoplach?.id) && hasAccess && (mapaLayout === 'trasa' || mapaLayout === 'misto-trasa') && useRealData,
        staleTime: Infinity,
        // Poll every 2s while route not yet available (BE calculates async)
        refetchInterval: (query) => {
            if (!query.state.data?.polyline) return 2000;
            return false;
        },
        refetchOnWindowFocus: false,
    });

    // Station GPS from jednotka data
    const stationGps = useMemo(() => {
        if (jednotka?.adresaLat && jednotka?.adresaLng) {
            return `${jednotka.adresaLat},${jednotka.adresaLng}`;
        }
        return undefined;
    }, [jednotka?.adresaLat, jednotka?.adresaLng]);

    // === Vyjezd2026 real-mode queries — aktivní jen pro preset vyjezd-2026 mimo mock režim ===
    const isVyjezd2026 = poplachConfig.preset === 'vyjezd-2026';
    const enableVyjezd2026Queries = useRealData && isVyjezd2026 && Boolean(aktivniPoplach?.id);

    const { data: odpovediVyjezd } = useQuery<Odpoved[], QueryError>({
        queryKey: ['odpovedi', aktivniPoplach?.id, deviceToken],
        queryFn: () => aktivniPoplach
            ? JsdhNotifikaceService.getOdpovediByPoplachId(aktivniPoplach.id, deviceToken || undefined)
            : Promise.resolve([]),
        enabled: enableVyjezd2026Queries,
        refetchInterval: 4000,
        refetchOnWindowFocus: false,
        staleTime: 3000,
    });

    // Zaloha z predchoziho poplachu (spec R6) - jen pro vyjezd-2026 mimo mock; ostatni presety
    // si ji nacitaji ve svych wrapperech.
    const zalohaJindeVyjezd = useZalohaJinde(
        enableVyjezd2026Queries ? aktivniPoplach?.id : undefined,
        deviceToken || undefined,
    );

    const { data: allTechnikaVyjezd } = useQuery<Technika[]>({
        queryKey: ['technika', 'active', deviceToken],
        queryFn: () => deviceToken
            ? TechnikaServiceApi.getActiveTechnikaWithDeviceToken(deviceToken)
            : TechnikaServiceApi.getActiveTechnika(),
        enabled: useRealData && isVyjezd2026,
        // Kiosk je bez obsluhy a nikdy neziska focus — bez intervalu by jedno selhane
        // nacteni (restart BE, RPi nabootoval driv nez backend) nechalo seznam prazdny
        // az do rucniho reloadu a vsechny karty by spadly do fallbacku bez fotky a SPZ.
        refetchInterval: 15000,
        staleTime: 10000,
        refetchOnWindowFocus: false,
    });

    const { data: technikaSourcesVyjezd } = useQuery<PoplachTechnikaResponse[]>({
        queryKey: ['udalost-technika', aktivniPoplach?.id, deviceToken],
        queryFn: () => aktivniPoplach
            ? JsdhNotifikaceService.getUdalostTechnika(aktivniPoplach.id, deviceToken || undefined)
            : Promise.resolve([]),
        enabled: enableVyjezd2026Queries,
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

    const { data: notifikaceLogVyjezd } = useQuery<PosilovaNotifikaceLogResponse[]>({
        queryKey: ['udalost-notifikace-log', aktivniPoplach?.id, deviceToken],
        queryFn: () => aktivniPoplach
            ? JsdhNotifikaceService.getNotifikaceLog(aktivniPoplach.id, deviceToken || undefined)
            : Promise.resolve([]),
        enabled: enableVyjezd2026Queries,
        refetchInterval: 5000,
        refetchOnWindowFocus: false,
        staleTime: 4000,
    });

// Polling dokud není blob neprázdný
    useEffect(() => {
        if (!aktivniPoplach?.id || mockType) return;

        if (audioBlob && audioBlob.size === 0 && !isFetchingAudio) {
            const interval = setInterval(() => {
                refetchAudio();
            }, 2000); // každé 2s

            return () => clearInterval(interval);
        }
    }, [aktivniPoplach?.id, audioBlob, isFetchingAudio, refetchAudio, mockType]);


// Detekce preview poplachu (id < 0 nebo token === "PREVIEW")
    const isPreview = useMemo(() => {
        return aktivniPoplach?.id !== undefined && (
            aktivniPoplach.id < 0 ||
            aktivniPoplach.token === "PREVIEW"
        );
    }, [aktivniPoplach?.id, aktivniPoplach?.token]);

// Přehrávání audia (zakázáno pro preview)
    useEffect(() => {
        // Nepřehrávat audio pro preview poplach ani pro mock režim
        if (isPreview || mockType) return;
        if (!aktivniPoplach?.casVyhlaseni || !audioBlob) return;
        if (audioBlob.size === 0) {
            return;
        }

        // Cleanup previous audio before creating new one to prevent memory leak
        if (audioRef.current) {
            audioRef.current.pause();
            audioRef.current = null;
        }

        const url = URL.createObjectURL(audioBlob);
        const audio = new Audio(url);
        audio.preload = "auto";
        audio.loop = true; // hraje pořád dokola

        audio.play().catch(() => {
            // Autoplay blocked by browser policy
        });
        audioRef.current = audio;
        setAudioLoaded(true);

        // Stopnout po vypočítané době (podle casVyjezdMinuty)
        const timeout = setTimeout(() => {
            audio.pause();
        }, audioDurationMinutes * 60 * 1000);

        // Cleanup
        return () => {
            clearTimeout(timeout);
            audio.pause();
            URL.revokeObjectURL(url);
            setAudioLoaded(false);
        };
    }, [aktivniPoplach?.id, aktivniPoplach?.casVyhlaseni, audioBlob, audioDurationMinutes, isPreview, mockType]);



    useEffect(() => {
        if (refreshConfig?.hodnotaJakoBool) {
            window.location.reload();
        }
    }, [refreshConfig]);

    // Po deaktivaci poplachu: zobrazit text, po casVyjezdMinuty přepnout na BlackScreen
    useEffect(() => {
        if (aktivniPoplach?.id) {
            // Poplach je aktivní — zapamatovat ID a resetovat idle
            prevPoplachId.current = aktivniPoplach.id;
            setPostAlertIdle(false);
            return;
        }
        // Cold start / reload bez předchozího poplachu — jít do idle okamžitě
        if (!prevPoplachId.current) {
            setPostAlertIdle(true);
            return;
        }
        // Po deaktivaci poplachu čekat casVyjezdMinuty (zobrazit odpovědi hasičů)
        const delayMinutes = jednotka?.casVyjezdMinuty || 10;
        const timer = setTimeout(() => {
            setPostAlertIdle(true);
        }, delayMinutes * 60 * 1000);
        return () => clearTimeout(timer);
    }, [aktivniPoplach?.id, jednotka?.casVyjezdMinuty]);

    // Detekce změny configVersion z device tokenu → reload stránky
    useEffect(() => {
        if (!deviceToken || deviceThemeData?.configVersion == null) return;
        if (lastConfigVersion.current === undefined) {
            lastConfigVersion.current = deviceThemeData.configVersion;
            return;
        }
        if (deviceThemeData.configVersion !== lastConfigVersion.current) {
            window.location.reload();
        }
    }, [deviceToken, deviceThemeData?.configVersion]);


    // Zobrazení toastu při chybě spojení - pouze jednou
    useEffect(() => {
        if (isErrorPoplach && !errorToastShown) {
            showToast("Chyba při spojení se serverem.", "warning");
            setErrorToastShown(true);
        } else if (!isErrorPoplach && errorToastShown) {
            // Reset když se spojení obnoví
            setErrorToastShown(false);
        }
    }, [isErrorPoplach, errorToastShown, showToast]);


    // Přístup odepřen - není deviceToken ani přihlášený uživatel
    if (!hasAccess) {
        return (
            <ThemeProvider theme={dashboardTheme}>
                <CssBaseline />
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                    <Container maxWidth="sm">
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            justifyContent="center"
                            minHeight="80vh"
                        >
                            <Paper
                                elevation={3}
                                sx={{
                                    p: 4,
                                    textAlign: 'center',
                                    borderRadius: 2,
                                    width: '100%',
                                }}
                            >
                                <Box
                                    sx={{
                                        bgcolor: 'error.light',
                                        borderRadius: '50%',
                                        width: 80,
                                        height: 80,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        mx: 'auto',
                                        mb: 3,
                                    }}
                                >
                                    <LockIcon sx={{ fontSize: 48, color: 'error.dark' }} />
                                </Box>

                                <Typography variant="h5" gutterBottom>
                                    Přístup odepřen
                                </Typography>

                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    Pro zobrazení poplachové stránky se přihlaste nebo použijte device token.
                                </Typography>

                                <Typography variant="body2" color="text.secondary">
                                    Device URL: <code>/poplach?deviceToken=VÁŠ_TOKEN</code>
                                </Typography>
                            </Paper>
                        </Box>
                    </Container>
                </Box>
            </ThemeProvider>
        );
    }

    if (isLoadingPoplach) {
        return (
            <ThemeProvider theme={dashboardTheme}>
                <CssBaseline />
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                    <DashboardSkeleton />
                </Box>
            </ThemeProvider>
        );
    }

    if (isErrorPoplach && !aktivniPredpoplach) {
        return (
            <ThemeProvider theme={dashboardTheme}>
                <CssBaseline />
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                    <ErrorWithRetry
                        title="Chyba spojení"
                        message="Nepodařilo se připojit k serveru. Zkontrolujte připojení k internetu."
                        onRetry={() => refetchPoplach()}
                        isRetrying={isFetchingPoplach}
                        maxWidth={false}
                    />
                </Box>
            </ThemeProvider>
        );
    }


    // Source dat: real query nebo mock
    const effectivePoplach = mockType ? mockData?.aktivniPoplach : aktivniPoplach;
    const effectivePoplachConfig = mockType
        ? (mockData?.poplachConfig ?? null)
        : poplachConfig;
    const effectiveJednotka = mockType ? mockData?.jednotka : jednotka;
    const effectiveRouteData = mockType ? mockData?.routeData : routeData;

    // EARLY RETURN: nový preset vyjezd-2026 — mimo Container + Layout (K0.3, K0.4)
    // Pracuje v mock režimu i v real režimu (s reálnými queries z výše)
    if (effectivePoplach && effectivePoplachConfig?.preset === 'vyjezd-2026') {
        const wrapper = (
            <Vyjezd2026Wrapper
                aktivniPoplach={effectivePoplach}
                odpovedi={mockType ? (mockData?.odpovedi ?? []) : (odpovediVyjezd ?? [])}
                zalohaJinde={mockType ? [] : zalohaJindeVyjezd}
                allTechnika={mockType ? (mockData?.allTechnika ?? []) : (allTechnikaVyjezd ?? [])}
                technikaSources={mockType ? (mockData?.technikaSources ?? []) : (technikaSourcesVyjezd ?? [])}
                notifikaceLog={mockType ? (mockData?.notifikaceLog ?? []) : (notifikaceLogVyjezd ?? [])}
                routeData={effectiveRouteData ?? null}
                jednotka={effectiveJednotka}
                poplachConfig={effectivePoplachConfig}
                deviceToken={deviceToken || undefined}
                isPreview={isPreview}
                orientace={orientaceParam}
            />
        );
        return (
            <ThemeProvider theme={dashboardTheme}>
                <CssBaseline />
                {orientaceParam === 'vyska' ? (
                    <RamecekNahleduNaVysku>{wrapper}</RamecekNahleduNaVysku>
                ) : (
                    // Ostrý kiosek: rodič wrapperu má výšku okna, wrapper sám 100 % rodiče.
                    <Box sx={{ height: '100vh' }}>{wrapper}</Box>
                )}
            </ThemeProvider>
        );
    }

    if (!aktivniPoplach) {
        return (
            <ThemeProvider theme={dashboardTheme}>
                <CssBaseline />
                <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
                    {aktivniPredpoplach ? (
                        <PredpoplachDisplay predpoplach={aktivniPredpoplach} deviceToken={deviceToken || undefined} />
                    ) : postAlertIdle && deviceThemeData?.idleDisplayMode === 'BLACK' ? (
                        <BlackScreen />
                    ) : (
                        <ZadnyPoplach/>
                    )}
                </Box>
            </ThemeProvider>
        );
    }

    return (
        <ThemeProvider theme={dashboardTheme}>
            <CssBaseline />
            <Box sx={{ bgcolor: 'background.default', height: '100vh', maxHeight: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {isPreview && (
                    <Box
                        sx={{
                            bgcolor: 'warning.main',
                            color: 'warning.contrastText',
                            py: 1,
                            px: 2,
                            textAlign: 'center',
                        }}
                    >
                        <Typography variant="h6" fontWeight="bold">
                            VZOROVÝ POPLACH - testovací zobrazení
                        </Typography>
                    </Box>
                )}
                <Container disableGutters maxWidth={false} sx={{ flex: 1, mt: 0.5, pl: 1, pr: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                    <Layout>
                        <DetailPoplachu
                            aktivniPoplach={aktivniPoplach}
                            showMapa={poplachConfig.sections.mapa}
                            mapConfig={poplachConfig.mapa}
                            hideTechnikaJednotky={poplachConfig.sections.technika}
                            mapaLayout={mapaLayout}
                            routeData={routeData}
                            stationGps={stationGps}
                            showCasVyhlaseni={poplachConfig.casVyhlaseni !== false}
                            deviceToken={deviceToken || undefined}
                        />

                        {aktivniPoplach && poplachConfig.sections.odpovedi && poplachConfig.preset !== 'technika-ucast' && poplachConfig.preset !== 'vyjezd-2026' && (
                            <OdpovediWrapper aktivniPoplach={aktivniPoplach} deviceToken={deviceToken || undefined} showSpecializace={poplachConfig.zobrazitSpecializace !== false} showDatumCasVyhlaseni={poplachConfig.datumCasVyhlaseni === true} displejStyl={poplachConfig.displejStylOdpovedi || 'STRIPE'} adaptivniLayout={poplachConfig.displejAdaptivniLayout === true} />
                        )}

                        {aktivniPoplach && poplachConfig.sections.technika && poplachConfig.preset !== 'technika-ucast' && poplachConfig.preset !== 'vyjezd-2026' && (
                            <TechnikaWrapper aktivniPoplach={aktivniPoplach} deviceToken={deviceToken || undefined} />
                        )}

                        {aktivniPoplach && poplachConfig.preset === 'technika-ucast' && (
                            <TechnikaUcastWrapper
                                aktivniPoplach={aktivniPoplach}
                                deviceToken={deviceToken || undefined}
                                ucastConfig={poplachConfig.ucast}
                                displejStyl={poplachConfig.displejStylOdpovedi || 'STRIPE'}
                                adaptivniLayout={poplachConfig.displejAdaptivniLayout === true}
                            />
                        )}
                    </Layout>
                </Container>
            </Box>
        </ThemeProvider>
    );
};

export default DashboardPage;
